import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { appAlert } from '../../lib/dialogs';

@observer
class EditExternalRaceDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            updateInProgress: {},
            raceData: {},
            showDeleteConfirm: false,
            isDeleting: false,
        };
        this.updateTimers = {};
    }

    componentDidMount() {
        const { race } = this.props;
        if (race) {
            // Format the date for the input field
            let raceDate = race.raceDate;
            if (raceDate) {
                raceDate = new Date(raceDate).toISOString().split('T')[0];
            }
            this.setState({
                raceData: {
                    ...race,
                    raceDate: raceDate || '',
                },
            });
        }
    }

    setUpdateInProgress = (fieldKey, inProgress) => {
        this.setState(prevState => ({
            updateInProgress: {
                ...prevState.updateInProgress,
                [fieldKey]: inProgress,
            },
        }));
    }

    handleFieldChange = (e) => {
        const { name, value } = e.target;
        const { race } = this.props;

        // Update local state immediately
        this.setState(prevState => ({
            raceData: {
                ...prevState.raceData,
                [name]: value,
            },
        }));

        // Auto-save with 750ms delay
        if (race && race.id) {
            this.setUpdateInProgress(name, true);

            // Clear existing timer for this field
            if (this.updateTimers[name]) {
                clearTimeout(this.updateTimers[name]);
            }

            // Capture values for the timeout closure
            const capturedName = name;
            const capturedValue = value;

            // Set new timer for auto-save
            this.updateTimers[name] = setTimeout(() => {
                this.doAutoSave({ name: capturedName, value: capturedValue });
            }, 750);
        }
    }

    doAutoSave = async ({ name, value }) => {
        const { race } = this.props;
        const { externalRaceResultStore } = this.props.stores;

        try {
            await externalRaceResultStore.updateExternalRaceResult(race.id, { [name]: value });
        } catch (error) {
            console.error('Error saving:', error);
        }

        this.setUpdateInProgress(name, false);
    }

    closeDrawer = () => {
        const { callback } = this.props;
        const { appState } = this.props.stores;

        if (callback) {
            callback();
        }
        appState.toggleDrawer();
    }

    toggleDeleteConfirm = () => {
        this.setState(prevState => ({
            showDeleteConfirm: !prevState.showDeleteConfirm,
        }));
    }

    deleteRace = async (removeRatings = false) => {
        const { race, callback } = this.props;
        const { externalRaceResultStore, appState } = this.props.stores;

        this.setState({ isDeleting: true });

        try {
            await externalRaceResultStore.deleteExternalRaceResult(race.id, removeRatings);
            if (callback) {
                callback();
            }
            appState.toggleDrawer();
        } catch (error) {
            console.error('Error deleting race:', error);
            (await appAlert('Failed to delete race: ' + error.message));
        } finally {
            this.setState({ isDeleting: false });
        }
    }

    render() {
        const { raceData, updateInProgress = {}, showDeleteConfirm, isDeleting } = this.state;
        const isSaving = Object.values(updateInProgress).some(v => v);

        return (
            <div class='edit-external-race-drawer'>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                        <div class='font-weight-lighter px-3 box-header mt-0 d-flex justify-content-between align-items-center'>
                            <span>
                                <i class='fa-solid fa-pen-to-square me-2' />
                                <Text id='admin.edit-race'>Edit Race</Text>
                            </span>
                            {isSaving && (
                                <small class='text-primary'>
                                    <i class='fa-solid fa-spinner fa-spin me-1' />
                                    <Text id='admin.saving'>Saving...</Text>
                                </small>
                            )}
                        </div>

                        <div class='px-3 mt-3'>
                            <div class='card'>
                                <div class='card-body'>
                                    <div class='row g-3'>
                                        {/* Race Name */}
                                        <div class='col-12'>
                                            <label class='form-label small'>
                                                <Text id='admin.race-name'>Race Name</Text>
                                            </label>
                                            <input
                                                type='text'
                                                class='form-control'
                                                name='raceName'
                                                value={raceData.raceName || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., Finnmarkslopet 2024'
                                            />
                                        </div>

                                        {/* Race Class Name */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>
                                                <Text id='admin.race-class'>Race Class</Text>
                                            </label>
                                            <input
                                                type='text'
                                                class='form-control'
                                                name='raceClassName'
                                                value={raceData.raceClassName || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., FL-500'
                                            />
                                        </div>

                                        {/* Race Date */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>
                                                <Text id='admin.date'>Date</Text>
                                            </label>
                                            <input
                                                type='date'
                                                class='form-control'
                                                name='raceDate'
                                                value={raceData.raceDate || ''}
                                                onInput={this.handleFieldChange}
                                            />
                                        </div>

                                        {/* Year */}
                                        <div class='col-md-4'>
                                            <label class='form-label small'>
                                                <Text id='admin.year'>Year</Text>
                                            </label>
                                            <input
                                                type='number'
                                                class='form-control'
                                                name='year'
                                                value={raceData.year || ''}
                                                onInput={this.handleFieldChange}
                                                min='1900'
                                                max='2100'
                                            />
                                        </div>

                                        {/* Distance */}
                                        <div class='col-md-4'>
                                            <label class='form-label small'>
                                                <Text id='admin.distance'>Distance (km)</Text>
                                            </label>
                                            <input
                                                type='number'
                                                class='form-control'
                                                name='distance'
                                                value={raceData.distance || ''}
                                                onInput={this.handleFieldChange}
                                                step='0.1'
                                            />
                                        </div>

                                        {/* Location */}
                                        <div class='col-md-4'>
                                            <label class='form-label small'>
                                                <Text id='admin.location'>Location</Text>
                                            </label>
                                            <input
                                                type='text'
                                                class='form-control'
                                                name='location'
                                                value={raceData.location || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., Alta'
                                            />
                                        </div>

                                        {/* Country */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>
                                                <Text id='admin.country'>Country</Text>
                                            </label>
                                            <input
                                                type='text'
                                                class='form-control'
                                                name='country'
                                                value={raceData.country || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., Norway'
                                            />
                                        </div>

                                        {/* Source */}
                                        <div class='col-md-6'>
                                            <label class='form-label small'>
                                                <Text id='admin.source'>Source</Text>
                                            </label>
                                            <input
                                                type='text'
                                                class='form-control'
                                                name='source'
                                                value={raceData.source || ''}
                                                onInput={this.handleFieldChange}
                                                placeholder='e.g., Official results'
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div class='col-12'>
                                            <label class='form-label small'>
                                                <Text id='admin.notes'>Notes</Text>
                                            </label>
                                            <textarea
                                                class='form-control'
                                                name='notes'
                                                value={raceData.notes || ''}
                                                onInput={this.handleFieldChange}
                                                rows='3'
                                                placeholder='Any notes about this race...'
                                            />
                                        </div>
                                    </div>

                                    {/* Auto-save message */}
                                    <div class='mt-3 text-muted small'>
                                        <i class='fa-solid fa-info-circle me-1' />
                                        <Text id='admin.changes-saved-automatically'>Changes are saved automatically</Text>
                                    </div>
                                </div>
                            </div>

                            {/* Danger Zone - Delete */}
                            <div class='card mt-3 border-danger'>
                                <div class='card-header bg-danger text-white'>
                                    <i class='fa-solid fa-triangle-exclamation me-2' />
                                    <Text id='admin.danger-zone'>Danger Zone</Text>
                                </div>
                                <div class='card-body'>
                                    {!showDeleteConfirm ? (
                                        <div class='d-flex justify-content-between align-items-center'>
                                            <div>
                                                <strong><Text id='admin.delete-race'>Delete this race</Text></strong>
                                                <p class='text-muted small mb-0'>
                                                    <Text id='admin.delete-race-warning'>This will remove the race and optionally all associated musher ratings.</Text>
                                                </p>
                                            </div>
                                            <button
                                                class='btn btn-outline-danger rounded-pill'
                                                onClick={this.toggleDeleteConfirm}
                                            >
                                                <i class='fa-solid fa-trash me-1' />
                                                <Text id='admin.delete'>Delete</Text>
                                            </button>
                                        </div>
                                    ) : (
                                        <div class='d-flex flex-column'>
                                            <div class='alert alert-danger mb-3'>
                                                <i class='fa-solid fa-triangle-exclamation me-2' />
                                                <strong><Text id='admin.are-you-sure'>Are you sure?</Text></strong>
                                                <p class='mb-0 mt-2'>
                                                    <Text id='admin.delete-race-confirm'>Choose how you want to delete this race:</Text>
                                                </p>
                                            </div>
                                            <div class='d-flex flex-column gap-2'>
                                                <button
                                                    class='btn btn-warning rounded-pill'
                                                    onClick={() => this.deleteRace(false)}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? (
                                                        <i class='fa-solid fa-spinner fa-spin me-1' />
                                                    ) : (
                                                        <i class='fa-solid fa-trash me-1' />
                                                    )}
                                                    <Text id='admin.delete-race-only'>Delete race only (keep ratings)</Text>
                                                </button>
                                                <button
                                                    class='btn btn-danger rounded-pill'
                                                    onClick={() => this.deleteRace(true)}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? (
                                                        <i class='fa-solid fa-spinner fa-spin me-1' />
                                                    ) : (
                                                        <i class='fa-solid fa-trash me-1' />
                                                    )}
                                                    <Text id='admin.delete-race-and-ratings'>Delete race AND remove ratings</Text>
                                                </button>
                                                <button
                                                    class='btn btn-outline-secondary rounded-pill'
                                                    onClick={this.toggleDeleteConfirm}
                                                    disabled={isDeleting}
                                                >
                                                    <i class='fa-solid fa-xmark me-1' />
                                                    <Text id='admin.cancel'>Cancel</Text>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Action buttons */}
                <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                    <div class='d-flex justify-content-end'>
                        <button
                            class='btn btn-secondary rounded-pill'
                            onClick={this.closeDrawer}
                        >
                            <i class='fa-solid fa-check me-1' />
                            <Text id='admin.done'>Done</Text>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default EditExternalRaceDrawer;
